import React, { useState, useEffect, useRef } from 'react';
import { parseSRT, getCurrentSubtitle } from '../../../utils/srtParser';
import './style.scss';

const AudioPlayer = ({ audioSrc, srtSrc, autoPlay = false, onEnded, showControls = true, key }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [subtitles, setSubtitles] = useState([]);
  const [currentSubtitle, setCurrentSubtitle] = useState(null);
  const [showSubtitles, setShowSubtitles] = useState(true);

  // Stop audio when component unmounts (when navigating away)
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, []);

  // Pause when an info-spot audio modal opens (so only one audio plays at a time)
  useEffect(() => {
    const handler = () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
    window.addEventListener('infoSpotAudioOpen', handler);
    return () => window.removeEventListener('infoSpotAudioOpen', handler);
  }, []);

  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // Load SRT file
  useEffect(() => {
    if (srtSrc) {
      fetch(srtSrc)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then(text => {
          const parsed = parseSRT(text);
          setSubtitles(parsed);
          console.log('SRT file loaded, subtitles:', parsed.length);
        })
        .catch(error => {
          console.error('Error loading SRT file:', error);
        });
    }
  }, [srtSrc]);

  // Update current subtitle based on playback time
  useEffect(() => {
    if (isPlaying) {
      const interval = setInterval(() => {
        if (audioRef.current) {
          const time = audioRef.current.currentTime;
          setCurrentTime(time);
          if (subtitles.length > 0) {
            const subtitle = getCurrentSubtitle(subtitles, time);
            setCurrentSubtitle(subtitle);
          }
        }
      }, 100);

      return () => clearInterval(interval);
    }
  }, [subtitles, isPlaying]);

  // Set initial volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.muted = isMuted;
    }
  }, []);

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      audioRef.current.muted = newVolume === 0;
      setIsMuted(newVolume === 0);
    }
  };

  const toggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    if (audioRef.current) {
      audioRef.current.muted = newMuted;
      if (!newMuted && volume === 0) {
        setVolume(0.5);
        audioRef.current.volume = 0.5;
      }
    }
  };

  // Auto-play if enabled - wait for scene image to load first
  useEffect(() => {
    const audio = audioRef.current;
    if (autoPlay && audio) {
      const tryPlay = () => {
        if (audio && audio.readyState >= 2) {
          const playPromise = audio.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                setIsPlaying(true);
                console.log('Audio started playing');
              })
              .catch(error => {
                console.error('Auto-play failed:', error);
                const handleUserInteraction = () => {
                  if (audio) {
                    audio.play()
                      .then(() => setIsPlaying(true))
                      .catch(err => console.error('Retry play failed:', err));
                  }
                };
                document.addEventListener('click', handleUserInteraction, { once: true });
                document.addEventListener('touchstart', handleUserInteraction, { once: true });
              });
          }
        }
      };

      const delayTimeout = setTimeout(() => {
        const handleCanPlay = () => {
          tryPlay();
        };

        audio.addEventListener('canplay', handleCanPlay);
        tryPlay();

        audio._handleCanPlay = handleCanPlay;
      }, 2000);

      return () => {
        clearTimeout(delayTimeout);
        if (audio && audio._handleCanPlay) {
          audio.removeEventListener('canplay', audio._handleCanPlay);
        }
      };
    }
  }, [autoPlay]);

  // Handle audio events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleVolumeEvt = () => {
      setVolume(audio.volume);
      setIsMuted(audio.muted);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentSubtitle(null);
      if (onEnded) onEnded();
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('volumechange', handleVolumeEvt);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('volumechange', handleVolumeEvt);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [onEnded]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const handleSeek = (e) => {
    if (audioRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percent = x / rect.width;
      audioRef.current.currentTime = percent * duration;
    }
  };

  const formatTime = (seconds) => {
    if (!isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderVolumeIcon = () => {
    if (isMuted || volume === 0) {
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
          <line x1="23" y1="9" x2="17" y2="15" />
          <line x1="17" y1="9" x2="23" y2="15" />
        </svg>
      );
    }
    if (volume < 0.5) {
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
        </svg>
      );
    }
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      </svg>
    );
  };

  return (
    <div className="audio-player-container">
      <audio ref={audioRef} src={audioSrc} preload="auto" />

      {showControls && (
        <div className="audio-controls">
          <button 
            className={`play-pause-btn ${isPlaying ? 'playing' : ''}`} 
            onClick={togglePlay}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 4.5V19.5L19 12L7 4.5Z" strokeLinejoin="round" />
              </svg>
            )}
          </button>
          
          <div className="ap-progress-wrap" onClick={handleSeek}>
            <div className="ap-progress-track">
              <div
                className="ap-progress-fill"
                style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
              >
                <div className="ap-progress-handle" />
              </div>
            </div>
          </div>
          
          <span className="time-display">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>

          <div className="volume-control">
            <button 
              className={`volume-btn ${isMuted ? 'muted' : ''}`} 
              onClick={toggleMute}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {renderVolumeIcon()}
            </button>
            <div className="volume-slider-container">
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="volume-slider"
              />
            </div>
          </div>
          
          <button
            className={`subtitle-toggle ${showSubtitles ? 'active' : ''}`}
            onClick={() => setShowSubtitles(!showSubtitles)}
            title={showSubtitles ? 'Hide subtitles' : 'Show subtitles'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="15" width="18" height="6" rx="2" />
              <path d="M7 18h2" />
              <path d="M12 18h5" />
              <path d="M12 5c-5 0-9 4-9 9" />
              <path d="M21 14c0-5-4-9-9-9" />
            </svg>
          </button>
        </div>
      )}

      {showSubtitles && currentSubtitle && (
        <div className="subtitle-display" key={currentSubtitle.id || currentSubtitle.startTime}>
          {currentSubtitle.text}
        </div>
      )}
    </div>
  );
};

export default AudioPlayer;

